import { Button } from "@/ingot";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  useAddCartLine,
  useAddChainOperation,
  useCart,
  useQuote,
  useDeleteCartItem,
  useUploadQuote,
} from "@/api/queries";
import { useProcesses } from "@/api/queries/processes";

import { UploadDropzone } from "@/components/UploadDropzone";
import { formatChainOpError, formatCartLineError } from "@/lib/cartErrors";
import { formatDimensions } from "@/lib/dimensionDisplay";
import { formatUploadError } from "@/lib/uploadErrors";
import { UploadErrorAdminInsight } from "@/components/UploadErrorAdminInsight";
import { useProcessTypeLabel } from "@/lib/useProcessTypeLabel";
import { findModuleForFilename } from "@/processes/registry";
import type { CartLine, Quote } from "@/api/types";

interface Props {
  lang: string;
  /** Slug of the quote currently being replaced. Used to look up
   *  carryover variants in the cart and to delete the old item once
   *  the new one is in place. */
  oldQuoteSlug: string;
  /** Process name (``process_type``) of the page hosting the modal.
   *  Drives the upload payload's ``processType`` so the BE picks the
   *  right analyzer. */
  processName: string;
  /** File extensions the dropzone advertises. Comes from
   *  ``module.accepted_file_types``. */
  acceptedFileTypes: string[];
  /** Per-file upload cap in bytes. Comes from the hosting module via
   *  ``maxUploadBytes`` — without it the dropzone falls back to its own
   *  20 MB default and rejects files the module itself allows (KAN-713). */
  maxBytes?: number;
  /** Localised modal title (e.g. "Nahradit soubor"). */
  title: string;
  /**
   * Called after a successful same-process replace, with the new quote
   * payload. Pages with their own routing supply this; when omitted
   * the modal navigates to ``/${lang}/quote/${newQuoteId}``.
   */
  onReplaced?: (newQuote: Quote) => void;
  onClose: () => void;
}

interface SnapshotOp {
  id: number;
  process_type: string;
  order_index: number;
  config_snapshot: Record<string, unknown> | null;
}

type FailedItem =
  | { kind: "line"; index: number; line: CartLine; reason: string }
  | { kind: "op"; index: number; op: SnapshotOp; reason: string }
  | { kind: "delete"; reason: string };

// Keys the BE cascades from the primary op when a new chain op is
// added (see ``cart_operations.py`` ``add_operation_to_item``). We
// strip these from the OLD chain op's ``config_snapshot`` before
// replay — otherwise we'd force the OLD primary's material/variant
// onto the NEW primary's chain op, which is wrong if the new file's
// catalog differs.
const PRIMARY_CASCADED_KEYS = new Set([
  "material_code",
  // Legacy flat axis mirror cascaded by pre-dimensions BE versions.
  "thickness_mm",
  "quantity",
  "delivery_code",
  // The BE folds the PARENT's variant axes into the chain op's
  // ``dimensions`` at add time — replaying them onto the NEW primary
  // would force the old file's axes.
  "dimensions",
]);

function chainOnlyConfig(
  snapshot: Record<string, unknown> | null,
): Record<string, unknown> | undefined {
  if (!snapshot) return undefined;
  const filtered = Object.fromEntries(
    Object.entries(snapshot).filter(([k]) => !PRIMARY_CASCADED_KEYS.has(k)),
  );
  return Object.keys(filtered).length > 0 ? filtered : undefined;
}

type Phase =
  | { kind: "idle" }
  | { kind: "uploading" }
  | { kind: "replayingLines"; total: number; current: number; failures: FailedItem[] }
  | { kind: "replayingOps"; total: number; current: number; failures: FailedItem[] }
  | { kind: "deleting"; failures: FailedItem[] }
  | {
      kind: "done";
      newQuote: Quote;
      failures: FailedItem[];
      linesCarried: number;
      opsCarried: number;
    };

interface CarriedSnapshot {
  lines: CartLine[];
  ops: SnapshotOp[];
}

export function ReplaceFileModal({
  lang,
  oldQuoteSlug,
  processName,
  acceptedFileTypes,
  maxBytes,
  title,
  onReplaced,
  onClose,
}: Props): JSX.Element {
  const { t: tQuote } = useTranslation("quote");
  const navigate = useNavigate();

  const processesQuery = useProcesses();
  const cartQuery = useCart();
  // KAN-414 — nahrazovaná položka NEMUSÍ být v košíku: dokud ji
  // zákazník nepotvrdí, leží v odkladišti. Košík je proto zdroj jen pro
  // varianty (ty bez potvrzení neexistují); operace cesty a existenci
  // položky bere čtení POLOŽKY, které platí v obou fázích.
  const oldQuoteQuery = useQuote(oldQuoteSlug);
  const upload = useUploadQuote();
  const addLine = useAddCartLine();
  const addOp = useAddChainOperation();
  const deleteItem = useDeleteCartItem();

  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [uploadError, setUploadError] = useState<unknown>(null);
  // Snapshot of what we tried to carry over, captured before mutations
  // started — so the summary can render rows even after the cart cache
  // is invalidated by the replay.
  const carriedSnapshot = useRef<CarriedSnapshot>({ lines: [], ops: [] });

  const inFlight =
    phase.kind === "uploading" ||
    phase.kind === "replayingLines" ||
    phase.kind === "replayingOps" ||
    phase.kind === "deleting";

  // ``maxBytes`` je jediný strop, který tahle komponenta zná (dostává
  // ho od volajícího spolu s dropzone); pro hlášku ho vracíme na MB.
  const errorMessage = formatUploadError(
    uploadError,
    tQuote,
    maxBytes ? Math.round(maxBytes / (1024 * 1024)) : undefined,
  );

  const handleClose = useCallback(() => {
    if (inFlight) return;
    onClose();
  }, [onClose, inFlight]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") handleClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose]);

  const onFilesChosen = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    // Smart-routing: a file for a different module (e.g. customer is
    // on the laser page and drops a .stl) jumps to that module's
    // landing instead of running the wrong analyzer. Carryover doesn't
    // apply here — different module, different catalog.
    const target = findModuleForFilename(processesQuery.data, file.name);
    if (target && target.name !== processName) {
      if (!target.is_available) {
        setUploadError(null);
        return;
      }
      onClose();
      navigate(`/${lang}/services/${target.slug}`);
      return;
    }

    setUploadError(null);
    setPhase({ kind: "uploading" });

    let newQuote: Quote;
    let newQuoteId: string;
    try {
      newQuote = await upload.mutateAsync({ file, lang, processType: processName });
      newQuoteId = newQuote.id;
    } catch (err) {
      setUploadError(err);
      setPhase({ kind: "idle" });
      return;
    }

    // Snapshot old lines + ops from the cart cache BEFORE we start
    // mutating anything. The primary op (order_index === 0) is
    // auto-recreated when the first line is added to the new quote, so
    // we filter it out — only chain ops on top of it need replay.
    const cart = cartQuery.data ?? (await cartQuery.refetch()).data;
    const oldItem = cart?.items.find((it) => it.id === oldQuoteSlug);
    const oldLines = oldItem?.lines ?? [];
    const oldOps: SnapshotOp[] = (
      oldItem?.operations ?? oldQuoteQuery.data?.operations ?? []
    )
      .filter((op) => op.order_index > 0)
      .map((op) => ({
        id: op.id,
        process_type: op.process_type,
        order_index: op.order_index,
        config_snapshot: op.config_snapshot,
      }));
    carriedSnapshot.current = { lines: oldLines, ops: oldOps };

    const failures: FailedItem[] = [];

    // --- Phase 1: replay cart lines onto the new quote ---------------
    if (oldLines.length > 0) {
      for (let i = 0; i < oldLines.length; i += 1) {
        const line = oldLines[i];
        setPhase({
          kind: "replayingLines",
          total: oldLines.length,
          current: i,
          failures,
        });
        try {
          await addLine.mutateAsync({
            itemSlug: newQuoteId,
            line: {
              material_code: line.material_code,
              quantity: line.quantity,
              // Carry the FULL variant identity (every declared axis,
              // colour, enum axes, …) through the canonical ``dimensions``
              // channel — the BE resolves the axes from it, so no
              // top-level scalar is needed.
              dimensions: { ...line.dimensions },
            },
          });
        } catch (err) {
          failures.push({
            kind: "line",
            index: i,
            line,
            reason: formatCartLineError(err, tQuote),
          });
        }
      }
    }

    // --- Phase 2: replay chain operations (e.g. anodize) -------------
    //
    // Skip if no lines actually landed — without a primary laser op the
    // backend will 404 every chain-op POST, which would just bloat the
    // failure list with noise. The user can re-add ops manually after
    // they configure a line on the new quote.
    const linesCarried =
      oldLines.length - failures.filter((f) => f.kind === "line").length;
    if (oldOps.length > 0 && linesCarried > 0) {
      for (let i = 0; i < oldOps.length; i += 1) {
        const op = oldOps[i];
        setPhase({
          kind: "replayingOps",
          total: oldOps.length,
          current: i,
          failures,
        });
        try {
          await addOp.mutateAsync({
            itemSlug: newQuoteId,
            payload: {
              process_type: op.process_type,
              // Send only chain-specific keys (e.g. anodize colour);
              // drop cascaded keys so the BE re-derives material /
              // axis values / qty from the NEW primary op.
              config: chainOnlyConfig(op.config_snapshot),
            },
          });
        } catch (err) {
          failures.push({
            kind: "op",
            index: i,
            op,
            reason: formatChainOpError(err, tQuote),
          });
        }
      }
    } else if (oldOps.length > 0 && linesCarried === 0) {
      // Surface this in the summary so the user knows ops were
      // intentionally skipped because no line could be carried.
      for (let i = 0; i < oldOps.length; i += 1) {
        failures.push({
          kind: "op",
          index: i,
          op: oldOps[i],
          reason: tQuote("replace.errors.opSkippedNoLines"),
        });
      }
    }

    // --- Phase 3: delete the old cart item --------------------------
    //
    // The new quote is already live, so a delete failure isn't fatal —
    // but we surface it in the summary so the user knows the old item
    // is still in their cart and can clean it up manually. Silently
    // swallowing the error would leave a phantom DXF on the invoice.
    // Maže se i nepotvrzená položka (KAN-414). Podmínka na „je v košíku"
    // by ji tady nechala ležet v odkladišti až do retenčního úklidu —
    // zákazník ji přitom právě nahradil jinou.
    if (oldItem || oldQuoteQuery.data) {
      setPhase({ kind: "deleting", failures });
      try {
        await deleteItem.mutateAsync(oldQuoteSlug);
      } catch (err) {
        failures.push({
          kind: "delete",
          reason: formatCartLineError(err, tQuote),
        });
      }
    }

    const opsCarried =
      oldOps.length - failures.filter((f) => f.kind === "op").length;

    setPhase({
      kind: "done",
      newQuote,
      failures,
      linesCarried,
      opsCarried,
    });

    // Nothing failed → close + finalize. The page-supplied
    // ``onReplaced`` callback takes precedence; otherwise fall back to
    // navigating to the new quote's /quote/:id route.
    if (failures.length === 0) {
      onClose();
      if (onReplaced) {
        onReplaced(newQuote);
      } else {
        navigate(`/${lang}/quote/${newQuoteId}`);
      }
    }
  };

  const handleContinue = () => {
    if (phase.kind !== "done") return;
    onClose();
    if (onReplaced) {
      onReplaced(phase.newQuote);
    } else {
      navigate(`/${lang}/quote/${phase.newQuote.id}`);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="replace-file-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      data-testid="replace-file-modal"
    >
      <div className="w-full max-w-2xl rounded-lg bg-bg p-5 shadow-lg">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3
              id="replace-file-title"
              className="text-base font-semibold text-ink"
            >
              {title}
            </h3>
            <p className="mt-1 text-sm text-ink-3">
              {tQuote("replace.subtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={inFlight}
            aria-label={tQuote("replace.close")}
            className="-mr-1 -mt-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-3 hover:bg-surface-2 hover:text-ink disabled:opacity-50"
            data-testid="replace-file-close"
          >
            <svg
              viewBox="0 0 16 16"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              aria-hidden="true"
            >
              <path d="M3 3 L13 13 M13 3 L3 13" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {phase.kind === "done" && phase.failures.length > 0 ? (
          <FailureSummary
            failures={phase.failures}
            linesCarried={phase.linesCarried}
            opsCarried={phase.opsCarried}
            onContinue={handleContinue}
          />
        ) : phase.kind === "replayingLines" ||
          phase.kind === "replayingOps" ||
          phase.kind === "deleting" ? (
          <ProgressPanel phase={phase} />
        ) : (
          <UploadDropzone
            maxFiles={1}
            disabled={phase.kind === "uploading"}
            onFilesChosen={onFilesChosen}
            errorMessage={errorMessage}
            statusLabel={
              phase.kind === "uploading" ? tQuote("upload.analyzing") : null
            }
            acceptedFileTypes={acceptedFileTypes}
            maxBytes={maxBytes}
          />
        )}
        <UploadErrorAdminInsight error={uploadError} lang={lang} />
      </div>
    </div>
  );
}

function ProgressPanel({
  phase,
}: {
  phase: Extract<
    Phase,
    { kind: "replayingLines" } | { kind: "replayingOps" } | { kind: "deleting" }
  >;
}): JSX.Element {
  const { t: tQuote } = useTranslation("quote");
  let label: string;
  if (phase.kind === "replayingLines") {
    label = tQuote("replace.replayingLines", {
      current: phase.current + 1,
      total: phase.total,
    });
  } else if (phase.kind === "replayingOps") {
    label = tQuote("replace.replayingOps", {
      current: phase.current + 1,
      total: phase.total,
    });
  } else {
    label = tQuote("replace.deleting");
  }
  return (
    <div
      className="rounded-2xl border-2 border-dashed border-border-strong bg-surface-2 p-8 text-center text-sm text-ink-2"
      data-testid="replace-file-progress"
    >
      {label}
    </div>
  );
}

function FailureSummary({
  failures,
  linesCarried,
  opsCarried,
  onContinue,
}: {
  failures: FailedItem[];
  linesCarried: number;
  opsCarried: number;
  onContinue: () => void;
}): JSX.Element {
  const { t: tQuote } = useTranslation("quote");
  const formatProcessType = useProcessTypeLabel();
  return (
    <div data-testid="replace-file-summary">
      <p className="text-sm text-ink-2">
        {tQuote("replace.summaryCarried", {
          count: linesCarried,
          opsCount: opsCarried,
        })}
      </p>
      <p className="mt-3 text-sm font-medium text-ink">
        {tQuote("replace.summaryFailedHeading", { count: failures.length })}
      </p>
      <ul className="mt-2 space-y-1 text-sm text-ink-3">
        {failures.map((f, i) => {
          if (f.kind === "line") {
            return (
              <li
                key={`line-${f.line.id}`}
                className="rounded-md bg-surface-2 px-3 py-2"
                data-testid="replace-file-failure-line"
              >
                <div className="mono text-ink-2">
                  {f.line.material_display_name || f.line.material_code} ·{" "}
                  {formatDimensions(f.line.dimensions)} ·{" "}
                  {f.line.quantity}×
                </div>
                <div className="mt-0.5 text-xs text-ink-3">{f.reason}</div>
              </li>
            );
          }
          if (f.kind === "op") {
            return (
              <li
                key={`op-${f.op.id}`}
                className="rounded-md bg-surface-2 px-3 py-2"
                data-testid="replace-file-failure-op"
              >
                <div className="text-ink-2">
                  {tQuote("replace.opLabel", {
                    processType: formatProcessType(f.op.process_type),
                  })}
                </div>
                <div className="mt-0.5 text-xs text-ink-3">{f.reason}</div>
              </li>
            );
          }
          return (
            <li
              key={`delete-${i}`}
              className="rounded-md bg-surface-2 px-3 py-2"
              data-testid="replace-file-failure-delete"
            >
              <div className="text-ink-2">
                {tQuote("replace.deleteFailureLabel")}
              </div>
              <div className="mt-0.5 text-xs text-ink-3">{f.reason}</div>
            </li>
          );
        })}
      </ul>
      <div className="mt-4 flex justify-end">
        <Button
          type="button"
          variant="primary"
          onClick={onContinue}
          data-testid="replace-file-continue"
        >
          {tQuote("replace.continue")}
        </Button>
      </div>
    </div>
  );
}
